import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #hoverOpenTimer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    /* Hover to open cart drawer on desktop — defer so trigger is in DOM */
    this.#bindHoverTrigger();
  }

  #bindHoverTrigger() {
    const bind = () => {
      const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
      if (trigger) {
        trigger.addEventListener('mouseenter', this.#handleTriggerMouseEnter);
        trigger.addEventListener('mouseleave', this.#handleTriggerMouseLeave);
        /* Cancel hover + close drawer on click so only navigation fires */
        trigger.addEventListener('click', this.#handleTriggerClick);
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bind, { once: true });
    } else {
      bind();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();

    const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (trigger) {
      trigger.removeEventListener('mouseenter', this.#handleTriggerMouseEnter);
      trigger.removeEventListener('mouseleave', this.#handleTriggerMouseLeave);
      trigger.removeEventListener('click', this.#handleTriggerClick);
    }
  }

  #handleTriggerClick = () => {
    /* Cancel any pending hover-open timer */
    if (this.#hoverOpenTimer) {
      clearTimeout(this.#hoverOpenTimer);
      this.#hoverOpenTimer = null;
    }
    /* Close drawer if hover had already opened it — let navigation take over */
    if (this.refs.dialog?.open) {
      this.closeDialog();
    }
  };

  #handleTriggerMouseEnter = () => {
    if (isMobileBreakpoint()) return;
    if (this.refs.dialog?.open) return;

    /* Only open on hover if cart has items */
    const cartIcon = document.querySelector('.header-actions__cart-icon--has-cart');
    if (!cartIcon) return;

    this.#hoverOpenTimer = setTimeout(() => {
      if (!isMobileBreakpoint()) this.showDialog();
    }, 500);
  };

  #handleTriggerMouseLeave = () => {
    if (this.#hoverOpenTimer) {
      clearTimeout(this.#hoverOpenTimer);
      this.#hoverOpenTimer = null;
    }
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    /* Mobile: use pink top toast + bottom-nav badge (see fabelle-mobile-cart-notify) — no drawer */
    if (isMobileBreakpoint()) return;
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
